import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  notification,
  Typography,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoAddOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import {
  createPatient,
  getAllPatients,
} from "../../utils/api-lists/patient.api";
import { DivRow } from "../../widgets/GeneralBody";
import { RoundButtonWithIcon } from "../AntCostomComponents/Buttons";
import { ModalInput } from "../AntCostomComponents/Inputs";
import PatientListItem from "./PatientListItem";

const { Title } = Typography;

const PatientList = () => {
  const location = useLocation();
  const { user } = useContext(UserContext);

  const [patients, setPatients] = useState([]);
  const [error, setError] = useState(location.state?.error || null);
  const [isPatientCreated, setIsPatientCreated] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { t } = useTranslation("common");
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id) return;

    const fetchPatients = async () => {
      try {
        const data = await getAllPatients(user.id);
        setPatients(data);
      } catch (err) {
        setError(err);
      }
    };

    fetchPatients();
  }, [isPatientCreated]);

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { first_name, last_name, email, phone } = values;

      await createPatient(user.id, first_name, last_name, email, phone);

      notification.success({
        message: t("success"),
        description: t("patient_created"),
        placement: "top",
        duration: 3,
      });

      setIsModalVisible(false);
      form.resetFields();
      setIsPatientCreated((prev) => !prev); // Refresh the list
    } catch (error) {
      notification.error({
        message: t("error"),
        description: t("patient_create_failed"),
        placement: "top",
        duration: 3,
      });
      setError(error?.message || error);
    }
  };

  return (
    <Card
      style={{
        background: "rgba(88, 80, 109, 0.30)",
        borderRadius: 16,
      }}
    >
      <DivRow className="header-patient-list">
        <Title
          level={4}
          style={{ color: "#fff", marginBottom: 24, cursor: "pointer" }}
          onClick={() => navigate(-1)}
        >
          ← {t("patient_list")}
        </Title>

        <RoundButtonWithIcon
          text={t("add_patient")}
          icon={IoAddOutline}
          onClick={() => {
            setIsModalVisible(true);
          }}
        />
      </DivRow>

      {patients
        .slice()
        .reverse()
        .map((patient, index) => (
          <PatientListItem key={patient.id} patient={patient} index={index} />
        ))}
      <Modal
        title={t("add_new_patient")}
        open={isModalVisible}
        onCancel={() => handleCancel()}
        footer={null}
        width={700}
        className="custom-dark-modal"
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("first_name")}
                name="first_name"
                rules={[
                  { required: true, message: t("first_name_required") },
                  { type: "string", message: t("must_be_string") },
                ]}
              >
                <ModalInput />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t("last_name")}
                name="last_name"
                rules={[
                  { required: true, message: t("last_name_required") },
                  {
                    type: "string",
                    message: t("must_be_string"),
                  },
                ]}
              >
                <ModalInput />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("phone_number")}
                name="phone"
                rules={[
                  {
                    required: true,
                    message: t("phone_number_required"),
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: t("phone_must_number"),
                  },
                ]}
              >
                <ModalInput />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t("email")}
                name="email"
                validateTrigger="onBlur"
                rules={[
                  {
                    type: "email",
                    message: "Enter a valid email",
                  },
                ]}
              >
                <ModalInput />
              </Form.Item>
            </Col>
          </Row>

          <div className="mt-4 flex justify-end">
            <Button
              type="primary"
              onClick={() => {
                handleSubmit();
              }}
              style={{ backgroundColor: "#8e44ad", borderColor: "#8e44ad" }}
            >
              {t("create")}
            </Button>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default PatientList;
